import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import {
  moveReservations,
  movedReservationsInfoSelector,
  reservationTemplatesSelector,
} from 'slices/reservationTemplate.slice';
import { useAppDispatch } from 'slices/store';
import type { TReservationTemplate, TType } from '@timeedit/types/lib/types';
import { TEInput } from '@timeedit/ui-components';
import { Button, DatePicker } from '@timeedit/ui-components/antd';
import './MoveReservation.scss';

interface MoveReservationProps {
  template: TReservationTemplate;
}

const MoveReservation = (props: MoveReservationProps) => {
  const { template } = props;
  const dispatch = useAppDispatch();
  const types: TType[] = useSelector(selectObjectTypesList);
  const reservationTemplates: TReservationTemplate[] = useSelector(
    reservationTemplatesSelector
  );
  const moveReservationsInfo = useSelector(movedReservationsInfoSelector);
  const [moveTo, setMoveTo] = useState<string>();
  const [count, setCount] = useState<number>();
  const [date, setDate] = useState<any>();
  const [templateType, setTemplateType] = useState<string>();

  const templateOptions = useMemo(
    () =>
      reservationTemplates
        .filter((templ) => templ.id !== template.id)
        .map((template) => ({
          value: String(template.id),
          label: template.name,
        })),
    [reservationTemplates, template]
  );
  const typeOptions = useMemo(
    () =>
      types.map((type) => ({
        value: String(type.id),
        label: type.name,
      })),
    [types]
  );
  const onMoveReservations = () => {
    /* Send the updated states to the server with an API-request including:
      {moveTo}, {count}, {date}, {templateType}  */
    const moveReservationsRequest = {
      reservationTemplate: Number(moveTo),
      count,
      date: parseInt(date.format('X')),
      type: Number(templateType),
    };
    dispatch(
      moveReservations({
        moveReservationsRequest,
        reservationTemplate: template,
      })
    );
  };
  return (
    <div>
      <div className="move-reservations">
        <TEInput.Select
          flow="column"
          label="Move to"
          popupMatchSelectWidth={false}
          wrapperStyle={{ width: '144px' }}
          style={{ width: '100%' }}
          value={moveTo}
          options={templateOptions}
          onChange={setMoveTo}
          data-testid="move-to"
        />
        <TEInput.InputNumber
          label="Count"
          tooltip="Count"
          flow="column"
          wrapperStyle={{ width: '90px' }}
          value={count}
          onChange={(e) => setCount(Number.parseInt(e?.toString() || '0'))}
          min={1}
          max={100}
          data-testid="count"
        />
        <TEInput.LabelWrapper
          label="Date"
          flow="column"
          wrapperStyle={{ width: '160px' }}
          data-testid="date"
        >
          <DatePicker
            size="small"
            value={date}
            onChange={setDate}
            style={{ width: '100%' }}
          />
        </TEInput.LabelWrapper>
        <TEInput.Select
          label="Type"
          flow="column"
          wrapperStyle={{ width: '144px' }}
          style={{ width: '100%' }}
          value={templateType}
          options={typeOptions}
          onChange={setTemplateType}
          data-testid="type"
        />
        <div className="col button">
          <Button size="small" onClick={onMoveReservations}>
            Move reservations
          </Button>
        </div>
      </div>
      {!!moveReservationsInfo && (
        <div>
          <p
            className="small-heading-text"
            style={{ marginBottom: 0, marginTop: 20 }}
          >
            Total number of reservations:{' '}
            {moveReservationsInfo.totalNumberOfReservations}
          </p>
          <p className="small-heading-text">
            Number of reservations moved:{' '}
            {moveReservationsInfo.numberOfReservationsMoved}
          </p>
          {moveReservationsInfo.failedReservations?.map(
            (failedReservation: any) => (
              <p key={failedReservation}>{failedReservation}</p>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MoveReservation;
